import { type AppInfo } from '../../../types';
import { detectOs } from '../../../utils/platform';
import { useEmbeddedCopy, useAnotherApp } from '../../../hooks/useEmbeddedCopy';
import { openLink, storeLinkFor } from '../../../utils/appLinks';
import EmbeddedStatusScreen from '../shared/StatusScreen';

/**
 * "Yivi not on this phone?": get it from the store, or scan elsewhere. Every
 * app gets the Get button; without a known store link it says there are no
 * install steps yet (`onNoStoreLink`).
 */
export default function EmbeddedPhoneInstallScreen({
  app,
  onOpenApp,
  onScanElsewhere,
  onNoStoreLink,
  onBack,
}: {
  app?: AppInfo;
  onOpenApp: () => void;
  onScanElsewhere?: () => void;
  onNoStoreLink: () => void;
  onBack?: () => void;
}) {
  const { t } = useEmbeddedCopy();
  const appName = app?.name ?? '';
  const store = storeLinkFor(appName, detectOs());

  return (
    <EmbeddedStatusScreen
      visual="app"
      app={app}
      title={t('embedded.phoneInstall.title', { app: appName })}
      sub={t('embedded.phoneInstall.sub')}
      primary={{
        label: t('embedded.phoneInstall.get', { app: appName }),
        onClick: () => (store ? openLink(store) : onNoStoreLink()),
      }}
      secondary={useAnotherApp(onBack)}
      links={[
        {
          label: t('embedded.launch.open', { app: appName }),
          onClick: onOpenApp,
        },
        ...(onScanElsewhere
          ? [
              {
                label: t('embedded.phoneInstall.scanOther'),
                onClick: onScanElsewhere,
              },
            ]
          : []),
      ]}
    />
  );
}
